import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { useCouple } from '../../contexts/CoupleContext';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import api from '../../services/api';
import 'react-datepicker/dist/react-datepicker.css';

const CreateCouple: React.FC = () => {
  const { createCouple } = useCouple();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Form state
  const [names, setNames] = useState<string>('');
  const [weddingDate, setWeddingDate] = useState<Date>(new Date());
  const [weddingLocation, setWeddingLocation] = useState<string>('');
  const [customUrl, setCustomUrl] = useState<string>('');
  const [story, setStory] = useState<string>('');
  const [fundingGoal, setFundingGoal] = useState<string>('0');

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [urlStatus, setUrlStatus] = useState<'checking' | 'available' | 'taken' | null>(null);

  const generateCustomUrl = () => {
    if (names && !customUrl) {
      const url = names
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '');
      setCustomUrl(url);
    }
  };

  // Check customUrl availability using GET /couples/url/:customUrl
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (customUrl) {
      setUrlStatus('checking');
      timeoutId = setTimeout(async () => {
        try {
          await api.get(`/couples/url/${customUrl}`);
          setUrlStatus('taken');
          toast.error('This URL is already taken. Please choose a different one.', {
            toastId: 'url-taken',
          });
        } catch (err: any) {
          if (err.response?.status === 404) {
            setUrlStatus('available');
            toast.success('This URL is available!', {
              toastId: 'url-available',
              autoClose: 3000,
            });
          } else {
            setUrlStatus(null);
            toast.error('Failed to check URL availability');
          }
        }
      }, 500);
    }
    return () => clearTimeout(timeoutId);
  }, [customUrl]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (urlStatus === 'taken') {
      toast.error('The custom URL is already taken. Please choose a different URL.');
      return;
    }

    try {
      setError(null);
      setLoading(true);

      const coupleData = {
        names,
        weddingDate: weddingDate.toISOString(),
        weddingLocation,
        customUrl,
        story,
        fundingGoal: Number(fundingGoal),
      };

      await createCouple(coupleData);
      setSuccess('Your wedding profile has been created successfully!');
      toast.success('Your wedding profile has been created successfully!');

      setTimeout(() => {
        navigate('/admin');
      }, 2000);
    } catch (err: any) {
      let errorMessage = 'Failed to create wedding profile. Please try again.';
      if (err.response?.status === 409) {
        errorMessage = err.response.data.message || 'The custom URL is already taken. Please choose a different couple name or edit the custom URL.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Create Your Wedding Profile
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Let's get started by setting up the basic information for your wedding.
            </p>
          </div>
        </div>

        <div className="mt-8 bg-white p-6 shadow rounded-lg">
          {error && (
            <div className="mb-6">
              <Alert
                type="error"
                message={error}
                dismissible
                onDismiss={() => setError(null)}
              />
            </div>
          )}

          {success && (
            <div className="mb-6">
              <Alert
                type="success"
                message={success}
              />
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              id="names"
              label="Couple Names"
              required
              value={names}
              onChange={(e) => setNames(e.target.value)}
              onBlur={generateCustomUrl}
              placeholder="e.g. Sarah & Michael"
              helperText="How you want your names to appear on your wedding page"
            />

            <div>
              <label htmlFor="wedding-date" className="block text-sm font-medium text-gray-700">
                Wedding Date
              </label>
              <div className="mt-1">
                <DatePicker
                  id="wedding-date"
                  selected={weddingDate}
                  onChange={(date: Date) => setWeddingDate(date)}
                  dateFormat="MMMM d, yyyy"
                  minDate={new Date()}
                  className="shadow-sm focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>

            <Input
              id="wedding-location"
              label="Wedding Location"
              value={weddingLocation}
              onChange={(e) => setWeddingLocation(e.target.value)}
              placeholder="e.g. Sunset Beach Resort, Malibu"
            />

            <div>
              <label htmlFor="custom-url" className="block text-sm font-medium text-gray-700">
                Custom URL
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  wedplanner.com/wedding/
                </span>
                <input
                  type="text"
                  id="custom-url"
                  required
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  placeholder="sarah-and-michael"
                  className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-pink-500 focus:border-pink-500 sm:text-sm border-gray-300"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {urlStatus === 'checking' && 'Checking availability...'}
                {urlStatus === 'available' && 'This URL is available!'}
                {urlStatus === 'taken' && 'This URL is taken. Please choose another.'}
                {!urlStatus && 'This will be the URL of your wedding page that you can share with guests. Edit if needed to make it unique.'}
              </p>
            </div>

            <Textarea
              id="story"
              label="Your Story"
              rows={4}
              value={story}
              onChange={(e) => setStory(e.target.value)}
              placeholder="Tell your love story. How did you meet? How was the proposal?"
              helperText="You can share this with your guests on your wedding page"
            />

            <div>
              <label htmlFor="funding-goal" className="block text-sm font-medium text-gray-700">
                Funding Goal (optional)
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  id="funding-goal"
                  min="0"
                  value={fundingGoal}
                  onChange={(e) => setFundingGoal(e.target.value)}
                  className="focus:ring-pink-500 focus:border-pink-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="0.00"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">USD</span>
                </div>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Set a target for your wedding fund (you can change this later)
              </p>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                loading={loading}
                disabled={loading || urlStatus === 'taken'}
              >
                Create Wedding Profile
              </Button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CreateCouple;